import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  useGetAmendmentByIdQueryV2,
  useResubmitPayrunAmendment,
} from '@xemplo/amendment-query';
import { ButtonType } from '@xemplo/button';
import { HeaderIconMode } from '@xemplo/header-icon';
import { DawnTriangleAlert } from '@xemplo/icons';
import { ModalBodyOverflow, ModalTheme } from '@xemplo/modal';
import { PromptProps, PromptType } from '@xemplo/prompts';
import { safeCallAsync } from '@xemplo/safe-call';
import { ToastType, useToast } from '@xemplo/toast';

import * as S from './payrun-resubmit-amendment.styles';
import { PayrunResubmitAmendmentProps } from './payrun-resubmit-amendment.types';
import { ResubmitReason } from './resubmit-reason';

export const useResubmitPrompt = (props: PayrunResubmitAmendmentProps) => {
  const { amendmentId, onSuccess } = props;
  const { data: amendment } = useGetAmendmentByIdQueryV2({ id: amendmentId });
  const [promptVisible, setPromptVisible] = useState(false);
  const { addToast } = useToast();
  const { id } = useParams();

  const { mutateAsync: resubmitAmendment } = useResubmitPayrunAmendment({
    payrunId: id ?? amendment?.payrunId ?? null,
    amendmentId: amendment?.amendmentId ?? null,
  });
  const [payload, setPayload] = useState<string>('');

  const initialPromptProps = useMemo<PromptProps>(() => {
    return {
      open: false,
      type: PromptType.General,
      theme: ModalTheme.Light,
      header: {
        title: 'Resubmit amendment',
        headerIcon: {
          icon: <DawnTriangleAlert />,
          mode: HeaderIconMode.ActionItem,
        },
      },
      body: {
        content: (
          <S.PromptBody>
            <div>Are you sure you want to resubmit this amendment? </div>
            <ResubmitReason setPayload={setPayload} />
          </S.PromptBody>
        ),
        overflow: ModalBodyOverflow.Extended,
      },
      footer: {
        btnPrimary: {
          label: 'Resubmit',
          ariaLabel: 'Resubmit button',
          onClickAsync: async () => {
            const [, error] = await safeCallAsync(
              resubmitAmendment({ description: payload }),
            );
            if (error) return;
            setPromptVisible(false);
            addToast({ text: 'Amendment resubmitted', type: ToastType.Confirmation });
            onSuccess?.();
          },
          type: ButtonType.Primary,
        },
        btnSecondary: {
          label: 'Cancel',
          ariaLabel: 'Cancel button',
          type: ButtonType.Secondary,
          onClick: () => setPromptVisible(false),
        },
      },
    };
  }, [resubmitAmendment, payload, addToast, onSuccess]);

  useEffect(() => {
    if (initialPromptProps.open) {
      setPromptVisible(true);
    }
  }, [initialPromptProps.open]);

  return { promptProps: initialPromptProps, promptVisible, setPromptVisible };
};
